import { motion } from "framer-motion";
import {
    ShieldCheck, Settings, BookOpen, ConciergeBell,
    Wallet, GraduationCap, Users, Library
} from "lucide-react";

const roles = [
    {
        icon: ShieldCheck,
        title: "Super Admin",
        desc: "Full control over the entire system — all features, all schools, all data.",
        color: "#6d28d9",
        bg: "#f5f3ff",
        border: "#ddd6fe",
    },
    {
        icon: Settings,
        title: "Admin",
        desc: "Manages day-to-day school operations with access to almost all features.",
        color: "#1d4ed8",
        bg: "#eff6ff",
        border: "#bfdbfe",
    },
    {
        icon: BookOpen,
        title: "Teacher",
        desc: "Records attendance, grades exams, uploads lessons, and communicates with parents.",
        color: "#0e7490",
        bg: "#ecfeff",
        border: "#a5f3fc",
    },
    {
        icon: ConciergeBell,
        title: "Receptionist",
        desc: "Manages all front-office activities including visitor logs and student check-ins.",
        color: "#92400e",
        bg: "#fffbeb",
        border: "#fde68a",
    },
    {
        icon: Wallet,
        title: "Accountant",
        desc: "Tracks student fees, manages payments, and reconciles all school expenses.",
        color: "#047857",
        bg: "#ecfdf5",
        border: "#a7f3d0",
    },
    {
        icon: GraduationCap,
        title: "Student",
        desc: "Views timetables, results, assignments, announcements, and exam schedules.",
        color: "#be185d",
        bg: "#fdf2f8",
        border: "#fbcfe8",
    },
    {
        icon: Users,
        title: "Parent",
        desc: "Monitors attendance, receives updates, and tracks academic progress in real time.",
        color: "#9a3412",
        bg: "#fff7ed",
        border: "#fed7aa",
    },
    {
        icon: Library,
        title: "Librarian",
        desc: "Manages the full library — book catalogue, issue/return, and student records.",
        color: "#3730a3",
        bg: "#eef2ff",
        border: "#c7d2fe",
    },
];

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07 } },
};

const item = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const UserRolesSection = () => (
    <section className="py-24 bg-background overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">

            {/* Header */}
            <div className="text-center mb-16 max-w-2xl mx-auto">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4">
                    Role-Based Access
                </span>
                <h2 className="text-3xl md:text-5xl font-heading font-extrabold text-foreground mb-4">
                    One Platform,{" "}
                    <span className="text-primary">Multiple Roles</span>
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Every user gets exactly the access they need — no more, no less. Predefined roles keep your school secure and organised.
                </p>
            </div>

            {/* Role cards */}
            <motion.div
                variants={container}
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
            >
                {roles.map((r) => (
                    <motion.div
                        key={r.title}
                        variants={item}
                        className="group relative rounded-2xl border-2 p-6 cursor-default transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                        style={{
                            background: r.bg,
                            borderColor: r.border,
                        }}
                    >
                        {/* Glow on hover */}
                        <div
                            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                            style={{ boxShadow: `0 0 32px 4px ${r.color}22` }}
                        />

                        {/* Icon */}
                        <div
                            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-sm transition-transform duration-300 group-hover:scale-110"
                            style={{ background: `${r.color}18`, border: `1.5px solid ${r.color}30` }}
                        >
                            <r.icon className="w-7 h-7" style={{ color: r.color }} />
                        </div>

                        {/* Badge */}
                        <span
                            className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest mb-3"
                            style={{ background: `${r.color}18`, color: r.color }}
                        >
                            {r.title}
                        </span>

                        {/* Title */}
                        <h3 className="font-heading font-bold text-xl text-foreground mb-2">{r.title}</h3>

                        {/* Description */}
                        <p className="text-sm text-slate-700 leading-relaxed">{r.desc}</p>

                        {/* Bottom accent bar */}
                        <div
                            className="absolute bottom-0 left-6 right-6 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{ background: r.color }}
                        />
                    </motion.div>
                ))}
            </motion.div>

            {/* Bottom callout */}
            <motion.div
                className="mt-14 text-center"
            >
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary/5 border border-primary/20 text-sm font-medium text-slate-700">
                    <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
                    Roles define <strong className="text-foreground">predefined permissions</strong> — keeping your school data safe and staff accountable.
                </div>
            </motion.div>

        </div>
    </section>
);

export default UserRolesSection;
